import React from 'react';
import { Check, Shield, Zap, RefreshCw, Database, Monitor, Settings, Wifi, HardDrive, Wrench, Home, AlertTriangle, FileText, Lightbulb, Cloud, Mail, Network } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

// Service Modal Component
const ServiceModal = ({ isOpen, onClose, language, serviceType }) => {
  
  // Service Content Configuration
  const getServiceContent = () => {
    const lang = language === 'de' ? 'de' : 'en';
    
    const services = {
      windows11: {
        de: {
          title: 'Windows 11 Upgrade-Service',
          subtitle: 'Professioneller Umstieg – sicher, schnell und stressfrei',
          intro: 'Windows 11 bringt Ihren Computer auf den neuesten Stand – mit verbesserter Leistung, moderner Oberfläche und erweiterten Sicherheitsfunktionen.',
          benefits: {
            title: 'Ihre Vorteile auf einen Blick',
            items: [
              { icon: Zap, title: 'Schnellerer Start und flüssigeres Arbeiten', description: 'für mehr Produktivität im Alltag' },
              { icon: RefreshCw, title: 'Neu gestaltete Benutzeroberfläche', description: 'klar, modern und intuitiv' },
              { icon: Shield, title: 'Mehr Sicherheit durch aktuelle Technologien', description: 'Schutz auf dem neuesten Stand' },
              { icon: Database, title: 'Nahtloser Übergang Ihrer Daten', description: 'alles bleibt erhalten, nur besser' },
              { icon: Check, title: 'Langfristige Unterstützung', description: 'optimal vorbereitet auf die digitale Zukunft' }
            ]
          },
          service: {
            title: 'Mein Service für Sie',
            subtitle: 'Ich übernehme den gesamten Umstieg – stressfrei, transparent und sicher:',
            steps: [
              { number: '01', title: 'Beratung und Systemprüfung', description: 'Ich prüfe, ob Ihr Computer bereit für Windows 11 ist.' },
              { number: '02', title: 'Datensicherung', description: 'Ihre Daten werden sicher gesichert und überprüft.' },
              { number: '03', title: 'Installation und Einrichtung', description: 'Ich installiere Windows 11 nach Ihren Bedürfnissen.' },
              { number: '04', title: 'Nachbetreuung', description: 'Auf Wunsch helfe ich bei Fragen und Feineinstellungen.' }
            ],
            footer: 'So können Sie direkt weiterarbeiten – ohne lange Unterbrechungen oder Verluste.'
          },
          contact: {
            title: 'Jetzt modernisieren',
            subtitle: 'Lassen Sie uns gemeinsam Ihren PC oder Ihr Unternehmen auf den neuesten Stand bringen.',
            cta: 'Kontaktieren Sie mich für Ihr individuelles Windows 11‑Upgrade!'
          }
        }
      },
      pcSetup: {
        de: {
          title: 'Einrichtung neuer PCs',
          subtitle: 'Professionelle Einrichtung – schlüsselfertig und transparent',
          intro: 'Ein neuer PC bedeutet mehr Leistung und Komfort – aber auch Aufwand bei der Einrichtung. Ich übernehme das für Sie, damit Sie direkt produktiv arbeiten können.',
          benefits: {
            title: 'Warum eine professionelle Einrichtung?',
            items: [
              { icon: Zap, title: 'Keine Zeit verlieren', description: 'Sie müssen sich nicht durch Installationen und Assistenten kämpfen' },
              { icon: Check, title: 'Alles Wichtige an Ort und Stelle', description: 'Daten, Programme und Zugänge sind direkt verfügbar' },
              { icon: Shield, title: 'Mehr Sicherheit von Anfang an', description: 'Aktuelle Updates, Virenschutz und sinnvolle Grundeinstellungen' },
              { icon: Settings, title: 'Individuell angepasst', description: 'Der PC wird auf Ihren Arbeitsalltag und Ihre Bedürfnisse optimiert' },
              { icon: Monitor, title: 'Ideal für kleine Unternehmen und Privatkunden', description: 'Weniger Technikstress, mehr Fokus auf das, was zählt' }
            ]
          },
          service: {
            title: 'Mein Einrichtungsservice für Ihren neuen PC',
            subtitle: 'Ich übernehme die komplette Einrichtung – schlüsselfertig und transparent:',
            steps: [
              { number: '01', title: 'Beratung & Planung', description: 'Wir klären gemeinsam, wofür der PC genutzt wird und was er können muss.' },
              { number: '02', title: 'Erstinstallation & Updates', description: 'Basisinstallation, Benutzerkonten, aktuelle Updates und Systemoptimierung.' },
              { number: '03', title: 'Datenübernahme', description: 'Übertragung Ihrer wichtigen Daten vom alten Gerät auf den neuen PC.' },
              { number: '04', title: 'Programme & E-Mail', description: 'Installation der benötigten Software, Office, E-Mail-Konten, Drucker & Netzlaufwerke.' },
              { number: '05', title: 'Sicherheit & Backup', description: 'Einrichtung von Virenschutz, Firewall und – auf Wunsch – Datensicherung.' },
              { number: '06', title: 'Feinschliff & Erklärung', description: 'Anpassung der Oberfläche, Schnellzugriffe, kurze Einweisung in die wichtigsten Funktionen.' }
            ],
            footer: 'So fühlt sich Ihr neuer PC nicht „neu und fremd", sondern vertraut und doch deutlich moderner an.'
          },
          contact: {
            title: 'Bereit für einen perfekt eingerichteten PC?',
            subtitle: 'Ob für Ihr Unternehmen, Ihr Homeoffice oder privat – ich richte Ihren neuen PC so ein, dass Sie direkt loslegen können.',
            cta: 'Schreiben Sie mir – ich kümmere mich um den Rest.'
          }
        }
      },
      virusProtection: {
        de: {
          title: 'Schutz vor Viren & Betrug',
          subtitle: 'Ganzheitlicher Schutz – technisch und menschlich',
          intro: 'In der digitalen Welt lauern täglich Gefahren. Ich schütze Sie mit modernster Technik und praxisnaher Schulung.',
          benefits: {
            title: 'Warum Sicherheit heute wichtiger denn je ist',
            items: [
              { icon: AlertTriangle, title: 'Phishing-Angriffe täglich', description: 'Seriös aussehende E-Mails täuschen selbst Vorsichtige' },
              { icon: Shield, title: 'Ransomware sperrt Ihre Daten', description: 'Erpressung durch Lösegeld-Forderungen' },
              { icon: Zap, title: 'Langsame PCs durch Spyware', description: 'Hintergrundprozesse verlangsamen Ihr System' },
              { icon: Database, title: 'Gestohlene Passwörter', description: 'Gefährdung von Bankkonten, E-Mails, Shopping' },
              { icon: AlertTriangle, title: 'Fake-Warnungen', description: 'Locken zu teuren „Support"-Hotlines' }
            ]
          },
          service: {
            title: 'Mein Viren- & Betrugsschutz-Service',
            subtitle: 'Ich schütze Sie ganzheitlich – technisch und menschlich:',
            steps: [
              { number: '01', title: 'Systemanalyse', description: 'Gründlicher Scan auf Viren, Trojaner, Spyware' },
              { number: '02', title: 'Reinigung', description: 'Entfernung aller Schadsoftware, Registrierungsaufräumen' },
              { number: '03', title: 'Sicherheitspaket', description: 'Virenschutz, Firewall, Updates, Passwortmanager' },
              { number: '04', title: 'Phishing-Schulung', description: 'Woran erkenne ich Betrugs-E-Mails?' },
              { number: '05', title: 'Datensicherung', description: 'Backup-Strategie für Notfälle' },
              { number: '06', title: 'Notfall-Plan', description: 'Was tun, wenn doch etwas passiert?' }
            ],
            footer: 'Ob präventiv oder nach einem Vorfall – Sie sind danach sicher und gut informiert.'
          },
          contact: {
            title: 'Sicherheit beginnt jetzt',
            subtitle: 'Ob Vorsorge oder Soforthilfe – lassen Sie uns Ihren PC gegen Viren und Betrug absichern.',
            cta: 'Sie sind nur einen Klick von mehr Sicherheit entfernt!'
          }
        }
      },
      routerSetup: {
        de: {
          title: 'Internet & Router-Einrichtung',
          subtitle: 'Beratung, Bestellung & Einrichtung – alles aus einer Hand',
          intro: 'Eine neue Internet-Verbindung bedeutet oft Stress mit Tarifen, Technik und Einrichtung. Ich begleite Sie durch den gesamten Prozess – von der Auswahl bis zur fertigen WLAN-Verbindung.',
          benefits: {
            title: 'Warum Unterstützung bei der Internet-Einrichtung sinnvoll ist',
            items: [
              { icon: Check, title: 'Tarif-Dschungel vermeiden', description: 'Gemeinsam finden wir einen Anschluss, der wirklich zu Ihrem Bedarf passt' },
              { icon: Zap, title: 'Technik ohne Stress', description: 'Kein Ärger mit komplizierten Anleitungen oder Hotline-Gesprächen' },
              { icon: Wifi, title: 'WLAN, das wirklich ankommt', description: 'Optimale Platzierung und Einrichtung des Routers für bessere Abdeckung' },
              { icon: Shield, title: 'Sicherheit von Anfang an', description: 'Sichere Passwörter, Verschlüsselung und sinnvolle Grundeinstellungen' },
              { icon: Monitor, title: 'Ideal für Büro & Zuhause', description: 'Stabil für Videokonferenzen, Cloud, Streaming, Smart-Home und mehr' }
            ]
          },
          service: {
            title: 'Beratung, Bestellung & Einrichtung – alles aus einer Hand',
            subtitle: 'Ich übernehme den gesamten Prozess – transparent, verständlich und praxisnah:',
            steps: [
              { number: '01', title: 'Bedarfsanalyse', description: 'Wir klären gemeinsam, wie viele Nutzer, Geräte und Anwendungen versorgt werden sollen.' },
              { number: '02', title: 'Tarif- & Anbieter-Empfehlung', description: 'Vorschläge für passende Anschlüsse (z. B. DSL, Kabel, Glasfaser – je nach Verfügbarkeit).' },
              { number: '03', title: 'Unterstützung bei der Bestellung', description: 'Hilfe bei der Beauftragung und Terminabstimmung mit dem Anbieter.' },
              { number: '04', title: 'Router- & WLAN-Einrichtung', description: 'Einrichtung des Routers, Vergabe sicherer WLAN-Namen und Passwörter, Optimierung der Position.' },
              { number: '05', title: 'Geräte anbinden', description: 'PCs, Notebooks, Smartphones, Drucker, Smart-TVs und weitere Geräte ins Netz bringen.' },
              { number: '06', title: 'Sicherheit & Komfort', description: 'Sinnvolle Router-Einstellungen, Gast-WLAN (falls gewünscht), kurze Einweisung in die wichtigsten Funktionen.' }
            ],
            footer: 'So wird aus „Wir brauchen neues Internet" eine stabile, saubere Lösung, die im Alltag einfach funktioniert.'
          },
          contact: {
            title: 'Bereit für eine stabile Verbindung?',
            subtitle: 'Gerne helfe ich Ihnen dabei, Ihre neue Internet-Verbindung von Anfang an richtig zu planen und professionell einzurichten.',
            cta: 'Schreiben Sie mir – ich kümmere mich um den Rest.'
          }
        }
      },
      dataBackup: {
        de: {
          title: 'Datensicherung & Wiederherstellung',
          subtitle: 'Rundumschutz für Ihre Daten – präventiv & reaktiv',
          intro: 'Datenverlust kann jeden treffen – plötzlich und ohne Vorwarnung. Ich schütze Ihre wichtigsten Daten mit professionellen Backup-Strategien und helfe auch bei der Rettung bereits verlorener Daten.',
          benefits: {
            title: 'Warum Datenverlust jeden treffen kann',
            items: [
              { icon: HardDrive, title: 'Festplattenausfall', description: 'Plötzlich und ohne Vorwarnung (jeder 2. PC innerhalb 5 Jahren)' },
              { icon: Shield, title: 'Viren & Ransomware', description: 'Daten werden gesperrt oder gelöscht' },
              { icon: AlertTriangle, title: 'Unachtsamkeit', description: 'Versehentliches Löschen wichtiger Dateien' },
              { icon: Database, title: 'Defekte Hardware', description: 'USB-Sticks, SD-Karten, externe Festplatten' },
              { icon: Wrench, title: 'Keine Backup-Strategie', description: '„Ich mach das später" wird oft zum Albtraum' }
            ]
          },
          service: {
            title: 'Mein Datensicherungs-Service – präventiv & reaktiv',
            subtitle: 'Ich biete Ihnen einen Rundumschutz für Ihre Daten – vorher und nachher:',
            steps: [
              { number: '01', title: 'Analyse Ihres Datenbestands', description: 'Was ist wirklich wichtig? Wo liegt es?' },
              { number: '02', title: 'Backup-Strategie', description: 'Automatische, verschlüsselte Sicherung (Cloud + lokal)' },
              { number: '03', title: 'Einrichtung', description: 'Backup-Software, externe Festplatten, Cloud-Dienste' },
              { number: '04', title: 'Test & Wiederherstellung', description: 'Regelmäßige Überprüfung – „Funktioniert das Backup?"' },
              { number: '05', title: 'Notfall-Datenrettung', description: 'Wiederherstellung auch bei defekter Hardware' },
              { number: '06', title: 'Schulung', description: 'Wie Sie selbst Ihre Daten schützen und retten' }
            ],
            footer: 'Ob präventive Absicherung oder Soforthilfe bei Datenverlust – Sie bekommen Ihre Daten zurück.'
          },
          contact: {
            title: 'Ihre Daten retten oder sichern lassen?',
            subtitle: 'Ob vorsorglich oder nach einem Verlust – ich hole Ihre Daten zurück oder sichere sie für die Zukunft.',
            cta: 'Ein Telefonat kann Ihre wichtigsten Daten retten!'
          }
        }
      },
      softwareHelp: {
        de: {
          title: 'Hilfe bei Softwareproblemen',
          subtitle: 'Systematische Lösungen – ohne Panik, ohne Umwege',
          intro: 'Programme starten nicht, Fehlermeldungen nerven, Updates scheitern – ich löse Ihre Softwareprobleme schnell und verständlich.',
          benefits: {
            title: 'Typische Software-Probleme, die ich löse',
            items: [
              { icon: Monitor, title: 'Programme starten nicht', description: 'Outlook, Word, Browser, PDF-Reader' },
              { icon: AlertTriangle, title: 'Fehlermeldungen', description: '„Fehlende DLL", „Lizenz abgelaufen", „Komponente fehlt"' },
              { icon: RefreshCw, title: 'Updates scheitern', description: 'Windows-Updates, Office-Updates, Software-Updates' },
              { icon: Zap, title: 'Langsame Programme', description: 'Excel mit großen Dateien, Photoshop, Videobearbeitung' },
              { icon: Wrench, title: 'Drucker-/Scanner-Probleme', description: '„Gerät nicht gefunden", Treiberfehler' }
            ]
          },
          service: {
            title: 'Mein Software-Hilfe-Service – Schritt für Schritt',
            subtitle: 'Ich löse Ihr Problem systematisch – ohne Panik, ohne Umwege:',
            steps: [
              { number: '01', title: 'Fehleranalyse', description: 'Was genau passiert? Welche Fehlermeldung kommt?' },
              { number: '02', title: 'Schnellhilfe', description: 'Sofortmaßnahmen für den schnellsten Erfolg' },
              { number: '03', title: 'Reparatur', description: 'Programme neu installieren, reparieren, konfigurieren' },
              { number: '04', title: 'Updates & Patches', description: 'Alles auf den neuesten Stand bringen' },
              { number: '05', title: 'Test & Schulung', description: 'Alles funktioniert? Sie wissen, wie es bleibt' },
              { number: '06', title: 'Prävention', description: 'So passiert es nicht wieder' }
            ],
            footer: 'Von „Startet nicht" bis „Funktioniert perfekt" – in einem Termin gelöst.'
          },
          contact: {
            title: 'Software wieder zum Laufen bringen',
            subtitle: 'Ob Outlook, Office, Browser oder Spezialsoftware – ich löse Ihr Problem, egal was kaputt ist.',
            cta: 'Ein Anruf, und Ihre Software funktioniert wieder!'
          }
        }
      },
      smartHome: {
        de: {
          title: 'Smart-Home-Unterstützung',
          subtitle: 'Von cleverer Technik zum funktionierenden System',
          intro: 'Alexa versteht Sie nicht mehr? Lampen blinken wild? Geräte verschiedener Hersteller funktionieren nicht zusammen? Ich bringe Ordnung in Ihr Smart Home.',
          benefits: {
            title: 'Typische Smart-Home-Probleme, die ich löse',
            items: [
              { icon: Home, title: 'Alexa/Google Home', description: 'Versteht keine Kommandos mehr, Geräte fallen aus' },
              { icon: Lightbulb, title: 'Philips Hue/Licht', description: 'Lampe blinkt, App-Probleme, WLAN-Verbindung weg' },
              { icon: Wifi, title: 'WLAN-Steckdosen', description: 'Tuya, Shelly, Meross – gehen nicht mehr online' },
              { icon: Settings, title: 'Smart-Rollos/Thermostate', description: 'Reagieren nicht, Automatisierungen fehlerhaft' },
              { icon: Wrench, title: 'Chaos-Mischung', description: 'Geräte verschiedener Hersteller funktionieren nicht zusammen' }
            ]
          },
          service: {
            title: 'Mein Smart-Home-Service – von A bis Z',
            subtitle: 'Ich mache aus „cleverer Technik" ein funktionierendes System – herstellerunabhängig:',
            steps: [
              { number: '01', title: 'Bestandsaufnahme', description: 'Was haben Sie schon? Was soll dazu?' },
              { number: '02', title: 'Netzwerk-Check', description: 'WLAN stark genug für Smart Home? Router optimieren' },
              { number: '03', title: 'Geräte-Integration', description: 'Alexa, Google, Apple – alles zusammenbringen' },
              { number: '04', title: 'Automatisierungen', description: '„Abends Licht aus", „Morgens Kaffee an"' },
              { number: '05', title: 'Test & Einweisung', description: 'Alle Funktionen einwandfrei? Bedienung verständlich?' },
              { number: '06', title: 'Sicherheit', description: 'Passwörter, Updates, Datenschutz' }
            ],
            footer: 'Von einzelner Lampe bis zum vollen Smart-Home-System – ich mache es einfach.'
          },
          contact: {
            title: 'Ihr Smart Home zum Laufen bringen',
            subtitle: 'Ob erste Lampe oder komplette Umstellung – ich mache Ihr Zuhause intelligent und einfach.',
            cta: 'Ein Termin, und Alexa hört wieder auf Sie!'
          }
        }
      },
      businessMaintenance: {
        de: {
          title: 'Systempflege & Fernwartung für Firmen',
          subtitle: 'Regelmäßige Wartung – Zuverlässig, Sicher, Produktiv',
          intro: 'Proaktive Systempflege statt reaktiver Feuerwehr – maßgeschneidert für Ihr Büro. Ich sorge dafür, dass Ihre IT läuft, wenn Sie arbeiten.',
          benefits: {
            title: 'Warum Firmen regelmäßige IT-Pflege brauchen',
            items: [
              { icon: AlertTriangle, title: 'Ausfälle kosten Geld', description: 'jede Stunde Stillstand schmerzt' },
              { icon: Shield, title: 'Sicherheitsrisiken', description: 'veraltete Systeme ohne aktuelle Updates' },
              { icon: Zap, title: 'Leistungseinbußen', description: 'vollgelaufene Festplatten, langsame PCs' },
              { icon: FileText, title: 'Compliance', description: 'DSGVO, GoBD verlangen aktuelle Systeme' },
              { icon: Wrench, title: 'Produktivitätsverlust', description: 'Mitarbeiter lösen IT-Probleme statt zu arbeiten' }
            ]
          },
          service: {
            title: 'Mein Wartungs- & Fernwartungskonzept für Firmen',
            subtitle: 'Proaktive Systempflege statt reaktiver Feuerwehr – maßgeschneidert für Ihr Büro:',
            steps: [
              { number: '01', title: 'Monatlicher System-Check', description: 'Fernwartung (15-30 Min.): Updates, Scans, Leistung' },
              { number: '02', title: 'Vierteljährliche Vor-Ort-Wartung', description: 'Gründliche Reinigung, Hardware-Check' },
              { number: '03', title: 'Priorisierter Support', description: 'Kurzfristig bei kritischen Problemen' },
              { number: '04', title: 'Rückmeldung', description: 'Sie wissen immer, wie gesund Ihre IT ist' },
              { number: '05', title: 'Notfall-Plan', description: 'Backup-Strategie, Wiederherstellungsprozess' }
            ],
            footer: 'Vermeiden Sie ungeplante Ausfälle – ich analysiere Ihren Wartungsbedarf und schaffe Kostenklarheit.'
          },
          contact: {
            title: 'IT-Ausfälle beenden – jetzt absichern',
            subtitle: 'Planbare Kosten statt unvorhersehbarer Pannen. Lassen Sie uns Ihren Wartungsbedarf analysieren.',
            cta: 'Ein Telefonat spart Ihrem Unternehmen Tausende Euro Stillstandskosten!'
          }
        },
        en: {
          title: 'System Maintenance & Remote Support for Businesses',
          subtitle: 'Regular Maintenance – Reliable, Secure, Productive',
          intro: 'Proactive system maintenance instead of reactive firefighting – tailored for your office. I ensure your IT runs when you work.',
          benefits: {
            title: 'Why Businesses Need Regular IT Maintenance',
            items: [
              { icon: AlertTriangle, title: 'Downtime Costs Money', description: 'every hour of standstill hurts' },
              { icon: Shield, title: 'Security Risks', description: 'outdated systems without current updates' },
              { icon: Zap, title: 'Performance Loss', description: 'full hard drives, slow PCs' },
              { icon: FileText, title: 'Compliance', description: 'GDPR, GoBD require current systems' },
              { icon: Wrench, title: 'Productivity Loss', description: 'employees solve IT problems instead of working' }
            ]
          },
          service: {
            title: 'My Maintenance & Remote Support Concept for Businesses',
            subtitle: 'Proactive system maintenance instead of reactive firefighting – tailored for your office:',
            steps: [
              { number: '01', title: 'Monthly System Check', description: 'Remote maintenance (15-30 min): Updates, scans, performance' },
              { number: '02', title: 'Quarterly On-Site Maintenance', description: 'Thorough cleaning, hardware check' },
              { number: '03', title: 'Priority Emergency Support', description: 'For critical issues requiring immediate attention' },
              { number: '04', title: 'Report', description: 'You always know how healthy your IT is' },
              { number: '05', title: 'Emergency Plan', description: 'Backup strategy, recovery process' }
            ],
            footer: 'Avoid unplanned outages – I analyze your maintenance needs and create cost clarity.'
          },
          contact: {
            title: 'End IT Outages – Secure Now',
            subtitle: 'Predictable costs instead of unpredictable breakdowns. Let us analyze your maintenance needs.',
            cta: 'One call saves your company thousands of euros in downtime costs!'
          }
        }
      },
      businessNetwork: {
        de: {
          title: 'Netzwerk- & Routereinrichtung für Firmen',
          subtitle: 'Business-taugliche Netzwerke – skalierbar, sicher, wartungsfreundlich',
          intro: 'Ich baue Netzwerke, die unternehmerisch denken – vom einfachen Router-Upgrade bis zum professionellen VLAN-Setup.',
          benefits: {
            title: 'Netzwerkprobleme, die Firmen teuer zu stehen kommen',
            items: [
              { icon: Wifi, title: 'Schwaches WLAN', description: 'kein Empfang z.B. im Lager, Konferenzraum oder Homeoffice' },
              { icon: Monitor, title: 'Drucker unerreichbar', description: 'Team läuft zur Druckerkonsole' },
              { icon: Cloud, title: 'Cloud-Zugang langsam', description: 'Dateifreigaben hängen' },
              { icon: AlertTriangle, title: 'Video-Calls stocken', description: 'Kundenverlust durch schlechte Qualität' },
              { icon: Shield, title: 'Gast-WLAN fehlt', description: 'Besucher nutzen Mitarbeiter-Netz' }
            ]
          },
          service: {
            title: 'Mein Firmennetzwerk-Service – Business-tauglich',
            subtitle: 'Ich baue Netzwerke, die unternehmerisch denken – skalierbar, sicher, wartungsfreundlich:',
            steps: [
              { number: '01', title: 'Standort-Analyse', description: 'Wo braucht Ihr Team stabiles Netz? (Büro, Lager, Außenbereich)' },
              { number: '02', title: 'Router-Profi-Konfiguration', description: 'VLANs, QoS, Gastnetz, VPN-Zugang' },
              { number: '03', title: 'WLAN-Optimierung', description: 'Access Points richtig platzieren, Kanäle optimieren' },
              { number: '04', title: 'Geräte-Integration', description: 'Drucker, Server, NAS, Cloud-Dienste, Telefonanlage' },
              { number: '05', title: 'Sicherheit', description: 'Firewall, sichere Passwörter, regelmäßige Updates' },
              { number: '06', title: 'Dokumentation & Schulung', description: 'Netzwerkplan, Zugangsdaten, Fehlerbehebung' }
            ],
            footer: 'Vom einfachen Router-Upgrade bis zum professionellen VLAN-Setup – Ihr Netzwerk passt zu Ihrem Business.'
          },
          contact: {
            title: 'Ihr Firmennetzwerk fit für die Zukunft machen',
            subtitle: 'Wie viele Arbeitsplätze? Homeoffice? Cloud-Dienste? Lassen Sie uns Ihr Netzwerkbedarf analysieren.',
            cta: 'Ein stabiles Netzwerk ist Ihr Produktivitätsmotor Nr. 1!'
          }
        }
,
        en: {
          title: 'Network & Router Setup for Businesses',
          subtitle: 'Business-Ready Networks – Scalable, Secure, Maintainable',
          intro: 'I build networks that think entrepreneurially – from simple router upgrades to professional VLAN setups.',
          benefits: {
            title: 'Network Problems That Cost Companies Dearly',
            items: [
              { icon: Wifi, title: 'WiFi Too Weak', description: 'in warehouse, meeting room, backyard' },
              { icon: Monitor, title: 'Printer Unreachable', description: 'team runs to printer console' },
              { icon: Cloud, title: 'Cloud Access Slow', description: 'file shares hang' },
              { icon: AlertTriangle, title: 'Video Calls Stutter', description: 'customer loss due to poor quality' },
              { icon: Shield, title: 'Guest WiFi Missing', description: 'visitors use employee network' }
            ]
          },
          service: {
            title: 'My Business Network Service – Enterprise-Ready',
            subtitle: 'I build networks that think entrepreneurially – scalable, secure, maintainable:',
            steps: [
              { number: '01', title: 'Site Analysis', description: 'Where does your team need stable network? (Office, warehouse, outdoor)' },
              { number: '02', title: 'Professional Router Config', description: 'VLANs, QoS, guest network, VPN access' },
              { number: '03', title: 'WiFi Optimization', description: 'Properly place access points, optimize channels' },
              { number: '04', title: 'Device Integration', description: 'Printers, servers, NAS, cloud services, phone system' },
              { number: '05', title: 'Security', description: 'Firewall, secure passwords, regular updates' },
              { number: '06', title: 'Documentation & Training', description: 'Network plan, credentials, troubleshooting' }
            ],
            footer: 'From simple router upgrades to professional VLAN setups – your network fits your business.'
          },
          contact: {
            title: 'Make Your Business Network Future-Ready',
            subtitle: 'How many workstations? Home office? Cloud services? Let us analyze your network needs.',
            cta: 'A stable network is your productivity engine #1!'
          }
        }
      },
      businessBackup: {
        de: {
          title: 'Datensicherung & Cloud-Lösungen für Firmen',
          subtitle: '3-2-1-Cloud-Backup-Konzept – DSGVO-konform & GoBD-sicher',
          intro: 'Die goldene Regel der Datensicherung – professionell umgesetzt für Ihr Unternehmen. Kein Datenverlust, keine Ausfallzeiten.',
          benefits: {
            title: 'Warum Firmen professionelle Datensicherung brauchen',
            items: [
              { icon: FileText, title: 'DSGVO-Pflicht', description: 'Kunden müssen Daten jederzeit abrufbar stellen können' },
              { icon: Database, title: 'GoBD-konform', description: 'Finanzdaten 10 Jahre revisionssicher archivieren' },
              { icon: AlertTriangle, title: 'Ransomware-Angriffe', description: '70% der Firmen zahlen oder müssen schlimmstenfalls schließen' },
              { icon: HardDrive, title: 'Hardware-Ausfall', description: '1 von 2 Servern fällt innerhalb 5 Jahren aus' },
              { icon: Zap, title: 'Produktivitätsverlust', description: 'Datenwiederherstellung dauert Tage/Wochen' }
            ]
          },
          service: {
            title: 'Mein 3-2-1-Cloud-Backup-Konzept für Firmen',
            subtitle: 'Die goldene Regel der Datensicherung – professionell umgesetzt:',
            steps: [
              { number: '01', title: '3 Kopien Ihrer Daten', description: 'Nie nur eine einzige Quelle' },
              { number: '02', title: '2 Medien', description: 'Lokale Festplatten + Cloud-Backup' },
              { number: '03', title: '1 Offline', description: 'Gegen Ransomware-Angriffe immun' },
              { number: '04', title: 'Cloud-Integration', description: 'OneDrive, SharePoint, Nextcloud Business' },
              { number: '05', title: 'Automatisierung', description: 'Tägliche, wöchentliche, monatliche Backups' },
              { number: '06', title: 'Test & Wiederherstellung', description: 'Regelmäßige Restore-Tests' }
            ],
            footer: '0% Datenverlust-Risiko durch mehrstufige Strategie. Wiederherstellung <4h.'
          },
          contact: {
            title: 'Ihre Datenstrategie – jetzt absichern',
            subtitle: 'Anzahl Arbeitsplätze? Rechnungsvolumen? Kritische Daten? Lassen Sie uns Ihre Backup-Lösung planen.',
            cta: 'Ein Datenverlust kostet im Schnitt 150.000 € – Ihre Absicherung deutlich weniger!'
          }
        }
,
        en: {
          title: 'Data Backup & Cloud Solutions for Businesses',
          subtitle: '3-2-1-Cloud-Backup Concept – GDPR Compliant & GoBD Secure',
          intro: 'The golden rule of data backup – professionally implemented for your company. No data loss, no downtime.',
          benefits: {
            title: 'Why Companies Need Professional Data Backup',
            items: [
              { icon: FileText, title: 'GDPR Obligation', description: 'customers must be able to retrieve data at any time' },
              { icon: Database, title: 'GoBD Compliant', description: 'archive financial data audit-proof for 10 years' },
              { icon: AlertTriangle, title: 'Ransomware Attacks', description: '70% of companies pay or may have to close' },
              { icon: HardDrive, title: 'Hardware Failure', description: '1 in 2 servers fails within 5 years' },
              { icon: Zap, title: 'Productivity Loss', description: 'data recovery takes days/weeks' }
            ]
          },
          service: {
            title: 'My 3-2-1-Cloud-Backup Concept for Businesses',
            subtitle: 'The golden rule of data backup – professionally implemented:',
            steps: [
              { number: '01', title: '3 Copies of Your Data', description: 'Never just one single source' },
              { number: '02', title: '2 Media', description: 'Local hard drives + cloud backup' },
              { number: '03', title: '1 Offline', description: 'Immune to ransomware attacks' },
              { number: '04', title: 'Cloud Integration', description: 'OneDrive, SharePoint, Nextcloud Business' },
              { number: '05', title: 'Automation', description: 'Daily, weekly, monthly backups' },
              { number: '06', title: 'Test & Recovery', description: 'Regular restore tests' }
            ],
            footer: '0% data loss risk through multi-level strategy. Recovery <4h.'
          },
          contact: {
            title: 'Secure Your Data Strategy Now',
            subtitle: 'Number of workstations? Invoice volume? Critical data? Let us plan your backup solution.',
            cta: 'Data loss costs an average of €150,000 – your protection much less!'
          }
        }
      },
      businessEmail: {
        de: {
          title: 'E-Mail & Cloud-Dienste für Firmen',
          subtitle: 'Professionelle Kommunikation – Microsoft 365, Google Workspace',
          intro: 'Komplettservice von der Beratung bis zur Schulung – maßgeschneidert für kleine Unternehmen. Professionelles Auftreten statt Gmail-Adresse.',
          benefits: {
            title: 'Warum Firmen professionelle E-Mail & Cloud brauchen',
            items: [
              { icon: Mail, title: 'Unprofessionelle Adressen', description: 'kundenname@gmail.com wirkt unseriös' },
              { icon: AlertTriangle, title: 'Kommunikations-Chaos', description: 'E-Mails gehen verloren, keine zentrale Ablage' },
              { icon: Zap, title: 'Zeitverlust', description: 'Dateien per E-Mail hin- und herschicken' },
              { icon: Shield, title: 'DSGVO-Risiko', description: 'Private Provider entsprechen nicht Business-Standards' },
              { icon: Settings, title: 'Skalierungsproblem', description: 'Neue Mitarbeiter? Neue E-Mails einrichten' }
            ]
          },
          service: {
            title: 'Mein E-Mail & Cloud-Service für Firmen',
            subtitle: 'Komplettservice von der Beratung bis zur Schulung – maßgeschneidert für kleine Unternehmen:',
            steps: [
              { number: '01', title: 'Bedarfsanalyse', description: 'Wie viele Mitarbeiter? Welche Abteilungen? Welche Tools?' },
              { number: '02', title: 'Domain & E-Mail', description: 'info@ihre-firma.de, vorname@ihre-firma.de' },
              { number: '03', title: 'Cloud-Paket-Auswahl', description: 'Microsoft 365, Google Workspace oder Nextcloud' },
              { number: '04', title: 'Einrichtung', description: 'Outlook, Teams, Kalender, Dateifreigaben' },
              { number: '05', title: 'Sicherheit', description: 'Zwei-Faktor, Anti-Spam, Archivierung, DSGVO-Compliance' },
              { number: '06', title: 'Schulung', description: 'Team weiß, wie alles funktioniert und bleibt' }
            ],
            footer: 'Professionelles Auftreten, spürbare Zeitersparnis, DSGVO-sicher.'
          },
          contact: {
            title: 'Ihr digitales Aushängeschild – jetzt professionalisieren',
            subtitle: 'Wie viele Mitarbeiter? Aktuelle E-Mail-Lösung? Welche Tools brauchen Sie? Lassen Sie uns starten.',
            cta: 'Professionelle E-Mail ist Ihr Türöffner zu größeren Kunden!'
          }
        }
,
        en: {
          title: 'Email & Cloud Services for Businesses',
          subtitle: 'Professional Communication – Microsoft 365, Google Workspace',
          intro: 'Complete service from consultation to training – tailored for small businesses. Professional appearance instead of Gmail address.',
          benefits: {
            title: 'Why Companies Need Professional Email & Cloud',
            items: [
              { icon: Mail, title: 'Unprofessional Addresses', description: 'customername@gmail.com appears unprofessional' },
              { icon: AlertTriangle, title: 'Communication Chaos', description: 'emails get lost, no central storage' },
              { icon: Zap, title: 'Time Loss', description: 'sending files back and forth via email' },
              { icon: Shield, title: 'GDPR Risk', description: 'private providers do not meet business standards' },
              { icon: Settings, title: 'Scaling Problem', description: 'new employees? set up new emails' }
            ]
          },
          service: {
            title: 'My Email & Cloud Service for Businesses',
            subtitle: 'Complete service from consultation to training – tailored for small businesses:',
            steps: [
              { number: '01', title: 'Needs Analysis', description: 'How many employees? Which departments? Which tools?' },
              { number: '02', title: 'Domain & Email', description: 'info@your-company.com, firstname@your-company.com' },
              { number: '03', title: 'Cloud Package Selection', description: 'Microsoft 365, Google Workspace or Nextcloud' },
              { number: '04', title: 'Setup', description: 'Outlook, Teams, calendar, file sharing' },
              { number: '05', title: 'Security', description: 'Two-factor, anti-spam, archiving, GDPR compliance' },
              { number: '06', title: 'Training', description: 'Team knows how everything works and stays' }
            ],
            footer: 'Professional appearance, noticeable time savings, GDPR secure.'
          },
          contact: {
            title: 'Professionalize Your Digital Business Card Now',
            subtitle: 'How many employees? Current email solution? Which tools do you need? Let us get started.',
            cta: 'Professional email is your door opener to bigger customers!'
          }
        }
      },
      businessSecurity: {
        de: {
          title: 'Sicherheitscheck & Virenschutz für Firmen',
          subtitle: '7-Punkte-Sicherheitsanalyse – ohne Panik, mit konkreten Lösungen',
          intro: 'Professionelle Sicherheitsprüfung für Ihr Unternehmen. Ich analysiere Ihre IT-Sicherheit und zeige konkrete Lösungen auf.',
          benefits: {
            title: 'Die größten IT-Sicherheitsrisiken für Firmen',
            items: [
              { icon: AlertTriangle, title: 'Phishing-Angriffe', description: '91% aller Breaches – Fake-Rechnung, Passwortdiebstahl' },
              { icon: Shield, title: 'Ransomware', description: '1 alle 11 Sekunden – Dateien gesperrt, Lösegeldforderung' },
              { icon: Wrench, title: 'Veraltete Software', description: '60% der Firmen – ungepatchte Sicherheitslücken' },
              { icon: FileText, title: 'Schwache Passwörter', description: '80% der Hacks – 123456, NameGeburtstag' },
              { icon: Database, title: 'Backup-Fehler', description: 'Backup funktioniert nicht, wenn man es braucht' }
            ]
          },
          service: {
            title: 'Mein Firmen-Sicherheitscheck – 7-Punkte-Analyse',
            subtitle: 'Professionelle Sicherheitsprüfung – ohne Panik, mit konkreten Lösungen:',
            steps: [
              { number: '01', title: 'Geräte-Scan', description: 'Nach Viren, Malware, Spyware auf allen PCs' },
              { number: '02', title: 'Netzwerk-Audit', description: 'Router-Sicherheit, offene Ports, Firewall' },
              { number: '03', title: 'Passwort-Check', description: 'Stärkeanalyse, Zwei-Faktor-Status' },
              { number: '04', title: 'Update-Status', description: 'Windows, Office, Treiber, Programme' },
              { number: '05', title: 'Backup-Test', description: 'Funktioniert Ihre Datensicherung wirklich?' },
              { number: '06', title: 'Phishing-Training', description: 'Mitarbeiter erkennen Betrugs-Mails' },
              { number: '07', title: 'Sicherheitsreport', description: 'Risiken + sofortige Maßnahmen' }
            ],
            footer: '99% Angriffsschutz durch mehrschichtige Verteidigung. DSGVO-konform dokumentiert.'
          },
          contact: {
            title: 'Ihr Sicherheitscheck – jetzt Risiken kennen',
            subtitle: 'Wie viele PCs? Server? Cloud-Dienste? Ich analysiere Ihre IT-Sicherheit – unverbindlich.',
            cta: 'Besser heute kleines Geld investieren als morgen großes Geld zu verlieren!'
          }
        }
,
        en: {
          title: 'Security Check & Virus Protection for Businesses',
          subtitle: '7-Point Security Analysis – Without Panic, With Concrete Solutions',
          intro: 'Professional security audit for your company. I analyze your IT security and show concrete solutions.',
          benefits: {
            title: 'The Biggest IT Security Risks for Companies',
            items: [
              { icon: AlertTriangle, title: 'Phishing Attacks', description: '91% of all breaches – fake invoice, password theft' },
              { icon: Shield, title: 'Ransomware', description: '1 every 11 seconds – files locked, ransom demand' },
              { icon: Wrench, title: 'Outdated Software', description: '60% of companies – unpatched security holes' },
              { icon: FileText, title: 'Weak Passwords', description: '80% of hacks – 123456, NameBirthday' },
              { icon: Database, title: 'Backup Failures', description: 'backup doesn\'t work when you need it' }
            ]
          },
          service: {
            title: 'My Business Security Check – 7-Point Analysis',
            subtitle: 'Professional security audit – without panic, with concrete solutions:',
            steps: [
              { number: '01', title: 'Device Scan', description: 'For viruses, malware, spyware on all PCs' },
              { number: '02', title: 'Network Audit', description: 'Router security, open ports, firewall' },
              { number: '03', title: 'Password Check', description: 'Strength analysis, two-factor status' },
              { number: '04', title: 'Update Status', description: 'Windows, Office, drivers, programs' },
              { number: '05', title: 'Backup Test', description: 'Does your data backup really work?' },
              { number: '06', title: 'Phishing Training', description: 'Employees recognize fraud emails' },
              { number: '07', title: 'Security Report', description: 'Risks + immediate actions' }
            ],
            footer: '99% attack protection through multi-layered defense. GDPR compliant documented.'
          },
          contact: {
            title: 'Your Security Check – Know Your Risks Now',
            subtitle: 'How many PCs? Servers? Cloud services? I analyze your IT security – non-binding.',
            cta: 'Better to invest small money today than lose big money tomorrow!'
          }
        }
      },
      businessPurchase: {
        de: {
          title: 'IT-Kaufberatung & Einrichtung für Firmen',
          subtitle: 'Unabhängige Beratung – schlüsselfertige Einrichtung',
          intro: 'Ich berate unabhängig und richte ein – damit Sie genau die richtige IT bekommen, ohne Überdimensionierung oder versteckte Kosten.',
          benefits: {
            title: 'Typische Kauf-Fallen für Firmen-IT',
            items: [
              { icon: AlertTriangle, title: 'Überdimensioniert', description: 'zu teure Hardware ohne Mehrwert' },
              { icon: Monitor, title: 'Unterdimensioniert', description: 'Leistung reicht nicht für Business-Anwendungen' },
              { icon: Settings, title: 'Inkompatibilität', description: 'Drucker nicht netzwerkfähig, WLAN zu schwach' },
              { icon: Wrench, title: 'Einrichtungs-Chaos', description: '4 Stunden Setup-Zeit pro Arbeitsplatz' },
              { icon: FileText, title: 'Versteckte Kosten', description: 'falsche Tintenmodelle, inkompatible Software' }
            ]
          },
          service: {
            title: 'Mein unabhängiger Beratungs- & Einrichtungsservice',
            subtitle: 'Rein beratend – Sie kaufen selbst, ich richte ein:',
            steps: [
              { number: '01', title: 'Bedarfsanalyse', description: 'Welche Software? Mitarbeiteranzahl? Wachstum?' },
              { number: '02', title: 'Unabhängige Empfehlung', description: 'PC, Monitor, Drucker, Zubehör – genau passend' },
              { number: '03', title: 'Marktübersicht', description: 'Bestes Preis-Leistungs-Verhältnis bei allen Händlern' },
              { number: '04', title: 'Kaufunterstützung', description: 'Checklisten, Mengenrabatt-Tipps, Finanzierungsoptionen' },
              { number: '05', title: 'Schlüsselfertige Einrichtung', description: 'Windows, Office, Netzwerk, Drucker, Cloud' },
              { number: '06', title: 'Schulung & Dokumentation', description: 'Team arbeitet sofort produktiv' }
            ],
            footer: 'Kostenersparnis durch genau richtige Dimensionierung. Produktiv ab Tag 1. Keine Follow-up-Kosten.'
          },
          contact: {
            title: 'IT-Investitionen intelligent planen',
            subtitle: 'Wie viele Arbeitsplätze? Welche Software? Budgetrahmen? Ich berate Sie unabhängig.',
            cta: 'Unabhängige Expertise statt Verkaufsdruck – das macht den Unterschied!'
          }
        }
,
        en: {
          title: 'IT Purchase Consulting & Setup for Businesses',
          subtitle: 'Independent Advice – Turnkey Setup',
          intro: 'I advise independently and set up – so you get exactly the right IT, without over-dimensioning or hidden costs.',
          benefits: {
            title: 'Typical Purchase Traps for Business IT',
            items: [
              { icon: AlertTriangle, title: 'Over-Dimensioned', description: 'too expensive hardware without added value' },
              { icon: Monitor, title: 'Under-Dimensioned', description: 'performance insufficient for business applications' },
              { icon: Settings, title: 'Incompatibility', description: 'printer not networkable, WiFi too weak' },
              { icon: Wrench, title: 'Setup Chaos', description: '4 hours setup time per workstation' },
              { icon: FileText, title: 'Hidden Costs', description: 'wrong ink models, incompatible software' }
            ]
          },
          service: {
            title: 'My Independent Consulting & Setup Service',
            subtitle: 'Purely advisory – you buy yourself, I set up:',
            steps: [
              { number: '01', title: 'Needs Analysis', description: 'Which applications? How many users? Growth plans?' },
              { number: '02', title: 'Hardware Recommendation', description: 'Business PCs, monitors, docking stations, printers' },
              { number: '03', title: 'Price Comparison', description: 'Business suppliers vs. retail – transparent' },
              { number: '04', title: 'You Buy', description: 'You decide where to buy – I don\'t earn commission' },
              { number: '05', title: 'I Set Up', description: 'Turnkey installation, data migration, software, network' },
              { number: '06', title: 'Training', description: 'Your team can work productively immediately' }
            ],
            footer: 'Cost savings through exactly right dimensioning. Productive from day 1. No follow-up costs.'
          },
          contact: {
            title: 'Plan IT Investments Intelligently',
            subtitle: 'How many workstations? Which software? Budget framework? I advise you independently.',
            cta: 'Independent expertise instead of sales pressure – that makes the difference!'
          }
        }
      },
      businessTraining: {
        de: {
          title: 'Schulung & Beratung für Firmen',
          subtitle: 'Verständlich erklärt, exakt auf Ihre Anforderungen abgestimmt',
          intro: 'Statt Standard-Schulung „von der Stange" bekommen Sie individuell zugeschnittene Inhalte – praxisnah und direkt umsetzbar.',
          benefits: {
            title: 'Wobei ich Ihre Mitarbeitenden unterstütze',
            items: [
              { icon: Mail, title: 'Microsoft 365 im Alltag', description: 'Outlook, Teams, OneDrive, SharePoint sinnvoll nutzen' },
              { icon: FileText, title: 'Dateiverwaltung & Zusammenarbeit', description: 'Struktur, Versionen, Freigaben' },
              { icon: Shield, title: 'Sicherheitsbewusstsein', description: 'Phishing erkennen, Passwörter sicher nutzen' },
              { icon: Zap, title: 'Effizientes Arbeiten', description: 'Vorlagen, Shortcuts, Automatisierungen' },
              { icon: Monitor, title: 'Umgang mit neuer Technik', description: 'neue PCs, Cloud-Dienste, Remote-Arbeit' }
            ]
          },
          service: {
            title: 'So laufen Schulung & Beratung ab',
            subtitle: 'Individuell zugeschnittene Inhalte statt Standard-Schulung „von der Stange":',
            steps: [
              { number: '01', title: 'Vorgespräch', description: 'Welche Programme nutzen Sie? Wo hakt es im Alltag?' },
              { number: '02', title: 'Zieldefinition', description: 'Was sollen Ihre Mitarbeitenden nach der Schulung konkret besser können?' },
              { number: '03', title: 'Individuelles Schulungskonzept', description: 'Inhalte, Dauer, Format (online, vor Ort, in kleinen Gruppen)' },
              { number: '04', title: 'Durchführung', description: 'Praxisnah, mit Beispielen aus Ihrem Unternehmensalltag' },
              { number: '05', title: 'Nachbereitung', description: 'Kurze Anleitungen/Spickzettel für den Alltag' },
              { number: '06', title: 'Optionale Follow-up-Termine', description: 'Fragen klären, Wissen festigen' }
            ],
            footer: 'Effizientere Abläufe, mehr Sicherheit, höhere Akzeptanz neuer Systeme. Motivierte Mitarbeitende durch beherrschte Technik.'
          },
          contact: {
            title: 'Schulung & Beratung nach Maß anfragen',
            subtitle: 'Ob einzelne Abteilung, kleines Team oder komplette Firma – ich entwickle mit Ihnen ein Schulungs- und Beratungskonzept, das genau zu Ihrer Arbeitsweise passt.',
            cta: 'Ein gut geschultes Team ist Ihre beste Investition in effizientes Arbeiten.'
          }
        }
,
        en: {
          title: 'Training & Consulting for Businesses',
          subtitle: 'Knowledge Transfer That Pays Off – For Teams and Processes',
          intro: 'I train and advise tailored to your everyday work – understandable, practical, effective.',
          benefits: {
            title: 'Why Employee Training Pays Off',
            items: [
              { icon: Lightbulb, title: 'Hidden Potential', description: 'many functions are never used' },
              { icon: AlertTriangle, title: 'Security Risks', description: 'employees click on phishing without training' },
              { icon: Zap, title: 'Time Wasters', description: 'manual processes instead of automation' },
              { icon: Settings, title: 'Software Chaos', description: 'everyone works differently – no standards' },
              { icon: Wrench, title: 'Support Bottleneck', description: 'always the same questions tie up resources' }
            ]
          },
          service: {
            title: 'My Training & Consulting Service for Businesses',
            subtitle: 'I train and advise tailored to your everyday work – understandable, practical, effective:',
            steps: [
              { number: '01', title: 'Needs Analysis', description: 'Where does your team struggle? Which processes are cumbersome?' },
              { number: '02', title: 'Training Concept', description: 'Microsoft 365, Teams, Outlook, security, processes' },
              { number: '03', title: 'On-site or Remote', description: 'Flexible format – workshop, screen sharing, documentation' },
              { number: '04', title: 'Practical Exercises', description: 'No theory marathon – applied to your everyday work' },
              { number: '05', title: 'Documentation', description: 'Cheat sheets, step-by-step guides for later' },
              { number: '06', title: 'Follow-up', description: 'Questions after training? I am available' }
            ],
            footer: 'One training day can save hundreds of hours annually. Measurable efficiency gain.'
          },
          contact: {
            title: 'Request Custom Training & Consulting',
            subtitle: 'Whether individual department, small team or entire company – I develop a training and consulting concept with you that exactly fits your work style.',
            cta: 'A well-trained team is your best investment in efficient work.'
          }
        }
      }
    };

    return services[serviceType]?.[lang] || null;
  };

  const content = getServiceContent();
  if (!content) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold" style={{ color: '#E53D4A' }}>
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-lg text-gray-600 dark:text-gray-400">
            {content.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-8 mt-6">
          {/* Intro */}
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {content.intro}
          </p>

          {/* Benefits */}
          <div>
            <h3 className="text-2xl font-bold mb-6" style={{ color: '#E53D4A' }}>
              {content.benefits.title}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {content.benefits.items.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#E53D4A] flex items-center justify-center">
                      <IconComponent className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Service Process */}
          <div>
            <h3 className="text-2xl font-bold mb-2" style={{ color: '#E53D4A' }}>
              {content.service.title}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              {content.service.subtitle}
            </p>
            <div className="space-y-4">
              {content.service.steps.map((step, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 p-4 rounded-lg border-l-4 border-[#E53D4A] bg-gray-50 dark:bg-gray-800"
                >
                  <div className="flex-shrink-0">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white"
                      style={{ background: '#E53D4A' }}
                    >
                      {step.number}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {step.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-700 dark:text-gray-300 mt-4 italic">
              {content.service.footer}
            </p>
          </div>

          {/* Contact CTA */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-6 rounded-lg">
            <h3 className="text-2xl font-bold mb-2" style={{ color: '#E53D4A' }}>
              {content.contact.title}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {content.contact.subtitle}
            </p>
            
            <a 
              href="mailto:kontakt@martinez.systems"
              className="block bg-[#E53D4A] hover:bg-[#d63340] text-white p-4 rounded-lg transition-colors duration-300 cursor-pointer"
            >
              <p className="font-semibold text-center">
                {content.contact.cta}
              </p>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceModal;
